import { INTEGRATION_DISPLAY_NAME_MAP, IntegrationServiceId } from '../../../common/integrations'
import {
  type CalendarIntegration,
  CalendarProvider,
  type MeetingPlatformIntegration,
  MeetingPlatformProvider,
  SchedulingIntegrationState,
  type UserIntegrations,
} from '../../../models/Scheduling'
import { type ExternalUserServiceIntegration } from '../../../models/User'
import {
  CALENDAR_OPTIONS,
  type CalendarSyncCopy,
  IntegrationConnectionStatus,
  NATIVE_PLATFORM_BY_CALENDAR,
} from '../../ProfileSettings/Scheduling/sections/schedulingIntegrationStatus'
import { isCapable } from '../flows/form/scheduleHelpers'

export { IntegrationConnectionStatus }

export interface CalendarOnboardingOption {
  provider: CalendarProvider
  serviceId: IntegrationServiceId
  label: string
  status: IntegrationConnectionStatus
  calendarSync?: CalendarSyncCopy
}

export interface MeetingPlatformOnboardingOption {
  provider: MeetingPlatformProvider
  serviceId?: IntegrationServiceId
  label: string
  viaLabel?: string
  isNative: boolean
  status: IntegrationConnectionStatus
}

export const CALENDAR_SERVICE_ID_BY_PROVIDER: { [key in CalendarProvider]: IntegrationServiceId } = {
  [CalendarProvider.GOOGLE]: IntegrationServiceId.GOOGLE_CALENDAR,
  [CalendarProvider.OUTLOOK]: IntegrationServiceId.OUTLOOK_CALENDAR,
  [CalendarProvider.EXCHANGE]: IntegrationServiceId.EXCHANGE_CALENDAR,
  [CalendarProvider.REDTAIL]: IntegrationServiceId.REDTAIL,
}

export const MEETING_PLATFORM_SERVICE_ID: Partial<Record<MeetingPlatformProvider, IntegrationServiceId>> = {
  [MeetingPlatformProvider.GOOGLE_MEET]: IntegrationServiceId.MEET,
  [MeetingPlatformProvider.MS_TEAMS]: IntegrationServiceId.TEAMS,
  [MeetingPlatformProvider.ZOOM]: IntegrationServiceId.ZOOM,
}

const resolveIntegrationStatus = (
  integration: CalendarIntegration | MeetingPlatformIntegration | undefined,
  calendarSyncGated = false,
): IntegrationConnectionStatus => {
  if (!integration || !integration.connected) {
    return IntegrationConnectionStatus.NOT_CONNECTED
  }
  if (isCapable(integration)) {
    return IntegrationConnectionStatus.READY
  }

  if (calendarSyncGated && integration.state === SchedulingIntegrationState.LIVE) {
    return IntegrationConnectionStatus.NEEDS_CALENDAR_SYNC
  }
  return IntegrationConnectionStatus.NEEDS_RECONNECT
}

export const resolveCalendarOptions = (
  integrations: UserIntegrations,
  isIntegrationAllowed: (serviceId: IntegrationServiceId) => boolean,
): CalendarOnboardingOption[] =>
  CALENDAR_OPTIONS.filter((option) => isIntegrationAllowed(option.serviceId)).map((option) => ({
    provider: option.provider,
    serviceId: option.serviceId,
    label: option.label,
    status: resolveIntegrationStatus(
      integrations.calendars.find((calendar) => calendar.provider === option.provider),
      option.calendarSyncGated,
    ),
    calendarSync: option.calendarSync,
  }))

export const resolveMeetingPlatformOptions = (
  integrations: UserIntegrations,
  isIntegrationAllowed: (serviceId: IntegrationServiceId) => boolean,
): MeetingPlatformOnboardingOption[] => {
  const options: MeetingPlatformOnboardingOption[] = []

  const selectedCalendar = integrations.selectedCalendar
  const nativeProvider = selectedCalendar ? NATIVE_PLATFORM_BY_CALENDAR[selectedCalendar] : undefined
  if (selectedCalendar && nativeProvider) {
    options.push({
      provider: nativeProvider,
      label: INTEGRATION_DISPLAY_NAME_MAP[MEETING_PLATFORM_SERVICE_ID[nativeProvider] as IntegrationServiceId] as string,
      viaLabel: `via ${INTEGRATION_DISPLAY_NAME_MAP[CALENDAR_SERVICE_ID_BY_PROVIDER[selectedCalendar]]}`,
      isNative: true,
      status: resolveIntegrationStatus(integrations.meetingPlatforms.find((platform) => platform.provider === nativeProvider)),
    })
  }

  if (isIntegrationAllowed(IntegrationServiceId.ZOOM)) {
    options.push({
      provider: MeetingPlatformProvider.ZOOM,
      serviceId: IntegrationServiceId.ZOOM,
      label: INTEGRATION_DISPLAY_NAME_MAP[IntegrationServiceId.ZOOM] as string,
      isNative: false,
      status: resolveIntegrationStatus(integrations.meetingPlatforms.find((platform) => platform.provider === MeetingPlatformProvider.ZOOM)),
    })
  }

  return options
}

export const getIntegrationEmail = (integrations: ExternalUserServiceIntegration[] | undefined, serviceId: IntegrationServiceId): string | undefined => {
  const integration = (integrations ?? []).find((candidate) => candidate.serviceId === serviceId)

  const calendarFallback = integration?.type === 'calendar' && integration.externalAccountId.includes('@') ? integration.externalAccountId : undefined
  return integration?.email ?? calendarFallback
}
